import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Button, Paper, Typography, IconButton, Tooltip, Alert } from '@mui/material';
import {
    CheckCircle as CompleteIcon,
    Description as DocIcon,
    Download as DownloadIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { useTracking } from '../../../hooks/useTracking';
import RichTextContent from '../../common/RichTextContent';

interface DocumentLessonPlayerProps {
    lesson: any;
    courseId?: number;
    userId: number;
    userEmail: string;
    onComplete: () => void;
}

/**
 * Leitet aus Lesson-Content den Render-Typ ab. Bestandsdaten haben kein
 * documentType (Editor setzte es nie), daher Filename-Extension-Fallback.
 * Exportiert fuer den Regression-Test (#151).
 */
export function inferDocumentType(content: any): 'pdf' | 'pptx' | 'docx' | 'xlsx' | 'url' | 'other' {
    if (content?.documentType === 'url') return 'url';
    if (content?.documentType && ['pdf', 'pptx', 'docx', 'xlsx', 'other'].includes(content.documentType)) {
        return content.documentType;
    }
    const name = String(content?.documentFileName || '').toLowerCase();
    if (name.endsWith('.pdf')) return 'pdf';
    if (name.endsWith('.pptx') || name.endsWith('.ppt')) return 'pptx';
    if (name.endsWith('.docx') || name.endsWith('.doc')) return 'docx';
    if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'xlsx';
    // Fallback fuer ganz alte Lessons ohne Filename: PDF — historisch hat
    // der Player das implizit angenommen.
    return 'pdf';
}

const officeLabels: Record<string, string> = {
    pptx: 'PowerPoint',
    docx: 'Word',
    xlsx: 'Excel',
    other: 'Datei',
};

const DocumentLessonPlayer: React.FC<DocumentLessonPlayerProps> = ({
    lesson,
    courseId,
    userId,
    userEmail,
    onComplete
}) => {
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string>('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string>('');
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const content = lesson.content || {};
    const docType = inferDocumentType(content);

    const { startLesson, completeLesson } = useTracking({
        lessonId: lesson.id,
        lessonTitle: lesson.title,
        courseId,
        userId,
        userEmail,
    });

    useEffect(() => {
        startLesson();
    }, [startLesson]);

    let { documentUrl, documentText, documentFileName } = content;
    let isMediaLibrary = false;
    let mediaUuid: string | undefined;

    if (documentUrl && documentUrl.startsWith('/api/media/')) {
        isMediaLibrary = true;
        mediaUuid = documentUrl.split('/').pop();
        documentUrl = mediaUuid;
    }

    // Load PDF from media library with authentication (existing behavior).
    useEffect(() => {
        if (isMediaLibrary && mediaUuid && docType === 'pdf') {
            const fetchPdf = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/preview/${mediaUuid}`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (response.ok) {
                        const blob = await response.blob();
                        setPdfBlobUrl(URL.createObjectURL(blob));
                    } else {
                        console.error('Failed to load PDF:', response.status);
                    }
                } catch (error) {
                    console.error('Error loading PDF:', error);
                }
            };
            fetchPdf();
        }
        return () => {
            if (pdfBlobUrl) {
                URL.revokeObjectURL(pdfBlobUrl);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaUuid, isMediaLibrary, docType]);

    // Fullscreen toggle (#151 — analog ScormPlayer).
    const toggleFullscreen = useCallback(() => {
        if (!isFullscreen) {
            const el = containerRef.current;
            if (el?.requestFullscreen) {
                el.requestFullscreen();
            } else if ((el as any)?.webkitRequestFullscreen) {
                (el as any).webkitRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            }
        }
    }, [isFullscreen]);

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        document.addEventListener('webkitfullscreenchange', handler);
        return () => {
            document.removeEventListener('fullscreenchange', handler);
            document.removeEventListener('webkitfullscreenchange', handler);
        };
    }, []);

    // Download-Helper fuer Office-Dateien — fetch mit Bearer, dann
    // a-Tag-Click. /api/media/download/{uuid} setzt Content-Disposition:
    // attachment, der Browser oeffnet den Save-Dialog.
    const handleDownload = async () => {
        if (!mediaUuid) return;
        setDownloading(true);
        setDownloadError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/download/${mediaUuid}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = documentFileName || 'download';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
            setDownloadError('Datei konnte nicht heruntergeladen werden.');
        } finally {
            setDownloading(false);
        }
    };

    const renderPdf = () => (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: '100%',
                height: isFullscreen ? '100vh' : '800px',
                bgcolor: isFullscreen ? '#000' : 'transparent',
            }}
        >
            <iframe
                ref={iframeRef}
                key={lesson?.id}
                src={isMediaLibrary ? pdfBlobUrl : documentUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="PDF Document"
            />
            <Tooltip title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}>
                <IconButton
                    onClick={toggleFullscreen}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        zIndex: 10,
                    }}
                    size="small"
                    aria-label="Vollbild"
                >
                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
            </Tooltip>
        </Box>
    );

    const renderOffice = () => (
        <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
            <DocIcon color="primary" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
                {documentFileName || 'Dokument'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {officeLabels[docType] || 'Office'}-Dateien können im Browser nicht direkt
                angezeigt werden. Bitte laden Sie die Datei herunter und öffnen Sie sie
                lokal in Ihrem Programm.
            </Typography>
            <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={downloading || !mediaUuid}
            >
                {downloading ? 'Wird heruntergeladen…' : 'Herunterladen'}
            </Button>
            {downloadError && (
                <Alert severity="error" sx={{ mt: 2 }}>{downloadError}</Alert>
            )}
        </Box>
    );

    const renderUrl = () => (
        <Button
            variant="contained"
            startIcon={<DocIcon />}
            href={documentUrl}
            target="_blank"
        >
            Dokument öffnen
        </Button>
    );

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
                {documentText && (
                    <RichTextContent
                        html={documentText}
                        sx={{ mt: 2, mb: 2 }}
                    />
                )}

                {(documentUrl || pdfBlobUrl) && (
                    <Box sx={{ mt: 2 }}>
                        {docType === 'pdf' && renderPdf()}
                        {(docType === 'pptx' || docType === 'docx' || docType === 'xlsx' || docType === 'other') && renderOffice()}
                        {docType === 'url' && renderUrl()}

                        {docType === 'pdf' && (isMediaLibrary ? pdfBlobUrl : documentUrl) && (
                            <Button
                                variant="outlined"
                                startIcon={<DocIcon />}
                                href={isMediaLibrary ? pdfBlobUrl : documentUrl}
                                target="_blank"
                                sx={{ mt: 2 }}
                            >
                                Dokument in neuem Tab öffnen
                            </Button>
                        )}
                    </Box>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<CompleteIcon />}
                    onClick={async () => {
                        try {
                            await completeLesson({ success: true });
                            onComplete();
                        } catch (error) {
                            console.error('Error completing lesson:', error);
                            onComplete();
                        }
                    }}
                >
                    Als abgeschlossen markieren
                </Button>
            </Box>
        </Box>
    );
};

export default DocumentLessonPlayer;
